import React from 'react'
import { Checkbox, Input } from 'antd'
import { map } from 'lodash'

import LinkButton from '../../core/components/sections/link-button'
import { formatDateFromNow } from '../../core/utils'

import { TypographyLinkWrapper } from '../../common/components/sections'
import StyledFlexInputGroup from '../../common/components/styled/styled-flex-input-group'
import {
  APP_ROUTES,
  EXCLUSION_STATUS,
  STATUS_FLAG_FIELD,
} from '../../common/constants'

import LocationStatusCheckbox from '../components/sections/location-status-checkbox'
import LocationTableCheckAllColumn from '../components/sections/locations-table-check-all-column'
import LocationsTableSelect from '../components/sections/locations-table-select'
import LocationsTableTreeSelect from '../components/sections/locations-table-tree-select'
import LOCATIONS_AVAILABLE_HOTKEYS from '../constants/locations-available-hotkeys'
import { checkIfLocationEditDisabled } from '../helpers'

import createBatchUpdateTableColumn from './create-batch-update-table-column'

const createLocationTableColumns = ({
  isDisabled,
  isTaskCurationDone,
  isTaskQaDone,
  cuisineTypeOptions,
  mainLocationTypeOptions,
  additionalLocationTypeOptions,
  awardOptions,
  result,
  areHotkeysEnabled,
  focusedItemId,
  selectedItemIds,
  toggleSelectItemId,
  toggleSelectAllItemIds,
}) => [
  {
    title: (
      <LocationTableCheckAllColumn
        locations={result}
        isTaskCurationDone={isTaskCurationDone}
        isTaskQaDone={isTaskQaDone}
        disabled={isDisabled}
      />
    ),
    render: ({ id, isCurationDone, isQaDone, isQcDone }) => {
      if (!isTaskCurationDone) {
        return (
          <LocationStatusCheckbox
            locationIds={[id]}
            value={isCurationDone}
            tooltip="Set location curation done"
            statusFlagFieldName={STATUS_FLAG_FIELD.IS_CURATION_DONE}
            disabled={isDisabled}
          />
        )
      }

      return !isTaskQaDone ? (
        <LocationStatusCheckbox
          locationIds={[id]}
          value={isQaDone}
          tooltip="Set location qa done"
          statusFlagFieldName={STATUS_FLAG_FIELD.IS_QA_DONE}
          disabled={isDisabled}
        />
      ) : (
        <LocationStatusCheckbox
          locationIds={[id]}
          value={isQcDone}
          tooltip="Set location qc done"
          statusFlagFieldName={STATUS_FLAG_FIELD.IS_QC_DONE}
          disabled={isDisabled}
        />
      )
    },
  },
  {
    title: 'Location',
    dataIndex: 'name',
  },
  {
    title: 'Website',
    render: ({ website }) => (
      <StyledFlexInputGroup compact>
        <Input readOnly disabled value={website} />
        <LinkButton link={website} shape="square" />
      </StyledFlexInputGroup>
    ),
  },
  createBatchUpdateTableColumn({
    allItemIds: map(result, 'id'),
    focusedItemId,
    selectedItemIds,
    toggleSelectItemId,
    selectItemIdHotkey: LOCATIONS_AVAILABLE_HOTKEYS.R.hotkey,
    toggleSelectAllItemIds,
    selectAllItemIdsHotkey: LOCATIONS_AVAILABLE_HOTKEYS.SHIFT_R.hotkey,
    areHotkeysEnabled,
    isDisabled,
  }),
  {
    title: 'Main Location Type',
    className: 'cell-editable field-mainLocationTypeId',
    render: ({ id, isCurationDone, isQaDone, isQcDone, mainLocationType }) => (
      <LocationsTableSelect
        locationId={id}
        value={mainLocationType?.id}
        fieldName="mainLocationTypeId"
        placeholder="Select Main Location Type..."
        options={mainLocationTypeOptions}
        isDisabled={
          isDisabled ||
          checkIfLocationEditDisabled({
            isTaskCurationDone,
            isTaskQaDone,
            isCurationDone,
            isQaDone,
            isQcDone,
          })
        }
      />
    ),
  },
  {
    title: 'Additional Location Type',
    className: 'cell-editable field-additionalLocationTypeIds',
    render: ({
      id,
      isCurationDone,
      isQaDone,
      isQcDone,
      additionalLocationTypes,
    }) => (
      <LocationsTableSelect
        locationId={id}
        value={map(additionalLocationTypes, 'id')}
        mode="multiple"
        fieldName="additionalLocationTypeIds"
        placeholder="Select Additional Location Type..."
        options={additionalLocationTypeOptions}
        isDisabled={
          isDisabled ||
          checkIfLocationEditDisabled({
            isTaskCurationDone,
            isTaskQaDone,
            isCurationDone,
            isQaDone,
            isQcDone,
          })
        }
      />
    ),
  },
  {
    title: 'Awards',
    className: 'cell-editable field-awardIds',
    render: ({ id, isCurationDone, isQaDone, isQcDone, awards }) => (
      <LocationsTableTreeSelect
        multiple
        locationId={id}
        value={map(awards, 'id')}
        fieldName="awardIds"
        placeholder="Select Awards..."
        treeData={awardOptions}
        isDisabled={
          isDisabled ||
          checkIfLocationEditDisabled({
            isTaskCurationDone,
            isTaskQaDone,
            isCurationDone,
            isQaDone,
            isQcDone,
          })
        }
      />
    ),
  },
  {
    title: 'Cuisine Type',
    className: 'cell-editable field-cuisineTypeId',
    render: ({ id, isCurationDone, isQaDone, isQcDone, cuisineType }) => (
      <LocationsTableTreeSelect
        locationId={id}
        value={cuisineType?.id}
        fieldName="cuisineTypeId"
        placeholder="Select Cuisine Type..."
        treeData={cuisineTypeOptions}
        isDisabled={
          isDisabled ||
          checkIfLocationEditDisabled({
            isTaskCurationDone,
            isTaskQaDone,
            isCurationDone,
            isQaDone,
            isQcDone,
          })
        }
      />
    ),
  },
  {
    title: 'AutoEat Status',
    render: ({ status }) => EXCLUSION_STATUS[status].label,
  },
  {
    title: 'Menus',
    render: ({ menuCurationTaskIds }) =>
      map(menuCurationTaskIds, id => (
        <TypographyLinkWrapper
          to={`${APP_ROUTES.MENUS.children.MENU_DETAILS.fullPath}/${id}`}
          target="_blank"
        >
          {id}
        </TypographyLinkWrapper>
      )),
  },
  {
    title: 'Added',
    dataIndex: 'createdAt',
    render: date => date && formatDateFromNow(date),
  },
  {
    title: 'Published',
    dataIndex: 'isPublished',
    render: isPublished => <Checkbox checked={isPublished} disabled />,
  },
  {
    title: 'Id',
    dataIndex: 'id',
  },
  {
    title: 'Autoeat Id',
    dataIndex: 'autoeatId',
  },
]

export default createLocationTableColumns
